import Backbone from 'backbone'
import template from './admin-nav.hbs'
import TreeFixer from './tree-fixer'
import DemoDataWizard from 'scoreboard/admin/demo-data/demo-data-wizard'
import appDisplay from 'scoreboard/components/app-display'
import config from 'scoreboard/components/config'
import permissions from 'scoreboard/components/permissions'
import license from 'scoreboard/components/license'
import _ from 'lodash'
import ConfirmPrompt from '../components/confirm-prompt'
import user from 'scoreboard/components/user'
import $ from 'jquery'
import globals from 'scoreboard/components/globals'
import LoadingDots from '../components/loading-dots'

/**
 * Renders the navigation pane for the admin section
 */
var AdminNav = Backbone.View.extend({

	section: 'admin',

	className: 'admin-nav',

	events: {
		'click .tree-fixer': 'treeFixer',
		'click .demo-data': 'demoData',
		'click .recalc-scores': 'recalcScores'
	},

	render: function() {
		this.loadingDots = new LoadingDots({ el: this.el }).render()

		let finishRender = (modifySomeDatasetPermission) => {
			this.renderAfterFetch(modifySomeDatasetPermission)
			this.rendered = true
			this.trigger('rendered')
		}

		if (globals.isOperationalMode()) {
			let url = 'api/datasets/modify-some-dataset-permission'
			$.get(url).then(modifySomeDatasetPermission => {
				finishRender(modifySomeDatasetPermission)
			})
		} else {
			// If not operational (eg. no databases defined, or logged in the
			// the non-database "root" installation for an SMC Impact server)
			// the call to /modify-some-dataset-permission will fail
			finishRender(false)
		}

		return this
	},

	/**
	 * Does the actual work of rendering the view after we've loaded whether
	 * the user has permission to modify one or more datasets
	 */
	renderAfterFetch: function(modifySomeDatasetPermission) {
		this.modifySomeDatasetPermission = modifySomeDatasetPermission
		this.loadingDots?.clear()

		// #30037 use "dashboards and strategy maps" if strategy maps are enabled
		let sharedDashboards = config.displayStrategyMaps ?
			'admin.sharedDashboards.strategyMaps' : 'admin.sharedDashboards'
		this.$el.empty().append(template({
			config: config,
			sharedDashboards
		}))
		this.secure()
	},

	/**
	 * Executes the given callback once the nav pane finishes rendering
	 */
	onceRendered: function(callback) {
		if (this.rendered) {
			callback()
		} else {
			this.once('rendered', callback)
		}
	},

	treeFixer: function() {
		appDisplay.dialog(new TreeFixer())
	},

	demoData: function() {
		var wizard = new DemoDataWizard()
		wizard.start()
	},

	recalcScores: function() {
		appDisplay.dialog(new ConfirmPrompt({
			titleKey: 'admin.recalcScores',
			messageKey: 'admin.recalcScoresConfirm',
			confirmLabel: 'admin.recalcScores.recalculate',
			cancelLabel: 'common.cancel',
			onConfirm: function() {
				return $.post('api/admin/recalc-scores')
			}
		}))
	},

	// this is called in the router
	select: function(item) {
		this.$('li').removeClass('is-selected')
		this.$('.' + item).addClass('is-selected')
	},

	// secure the display by hiding stuff the user doesn't have permission to view
	secure: function() {
		if (user.model.get('publicReadOnlyUser')) {
			this.$('.alert-preferences').addClass('is-not-displayed')
		}

		this.secureUsersGroupsPermissions()

		// Data Import & Export section
		if (!license.dashboards) {
			this.$('.shared-dashboards').addClass('is-not-displayed')
		}
		// note: with #48947, license.forms is based on license.datasets
		if (!license.forms) {
			this.$('.shared-forms').addClass('is-not-displayed')
		}
		if (user.isStrictViewer()) {
			this.$('.data-import-export').addClass('is-not-displayed')
			// add class to children of this section to hide them
			this.$('.data-import-export').find('li').addClass('is-not-displayed')
		}
		// #29125 hide imports and import connections for view only user
		if (permissions.readOnly()) {
			this.$('.imports, .import-connections').addClass('is-not-displayed')
		}

		// configuration section
		// #20029 either appAdmin or serverAdmin permission is required for Application Administration
		if (!permissions.appAdmin() && !permissions.serverAdmin()) {
			this.$('.app').addClass('is-not-displayed')
		}
		if (!permissions.serverAdmin()) {
			this.$('.server, .scoreboard-databases, .licenses, .selective-replication').addClass('is-not-displayed')
		}
		if (!(license.datasets && permissions.appAdmin())) {
			this.$('.datasets-trees').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin() && !permissions.serverAdmin() && !permissions.calendars()) {
			this.$('.configuration').addClass('is-not-displayed')
			// also hide the children of this section
			this.$('.configuration').find('li').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin()) {
			this.$('.selective-replication').addClass('is-not-displayed')
		}
		// #48181 hide Admin | Scoring Types page for SMS
		if (config.sms) {
			this.$('.scoring-types').addClass('is-not-displayed')
		}

		// monitoring section
		if (!permissions.serverAdmin()) {
			this.$('.diagnostics').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin()) {
			this.$('.current-user-activity, .logs').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin() && !permissions.serverAdmin()) {
			this.$('.background-process-status').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin() && !permissions.serverAdmin()) {
			this.$('.monitoring').addClass('is-not-displayed')
		}

		// advanced

		// #27617 only display "Mass Edit" for users with edit structure or
		// edit owner updaters (based on permissions used in #14567)
		let massEdit = permissions.anyStructure() || permissions.anyOwnerUpdaters()
		if (!massEdit) {
			this.$('.mass-edit').addClass('is-not-displayed')
		}
		// ticket:17347 for sms, only allow users with serverAdmin to recalc
		var recalc = (permissions.appAdmin() && !config.sms) || (permissions.serverAdmin() && config.sms)
		if (!recalc) {
			this.$('.recalc-scores').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin() && !permissions.readOnlySqlConsole()) {
			this.$('.sql-console').addClass('is-not-displayed')
		}
		// generate demo data is allowed for built in admin or appAdmin in development
		var generateData = user.model.get('username') === 'admin' || (permissions.appAdmin() && config.development)
		if (!generateData) {
			this.$('.demo-data').addClass('is-not-displayed')
		}
		if (!permissions.appAdmin() && !permissions.serverAdmin()) {
			this.$('.tree-fixer').addClass('is-not-displayed')
		}
		if (!recalc && !permissions.appAdmin() &&
			!permissions.readOnlySqlConsole() && !massEdit &&
			!permissions.bookmarks()) {
			this.$('.advanced').addClass('is-not-displayed')
		}

		// issue #5754 hide admin screens not included initial implementation
		this.$('.owners-updaters').addClass('is-not-displayed')

		// issue #15542 hide broadcast alert if user does not have Modify Bookmarks and Personal Settings
		if (!permissions.bookmarks()) {
			this.$('.broadcast-alert').addClass('is-not-displayed')
		}

		// Mark the last visible list item of the last visible section
		// so the css can give it a closing border (admin-nav.scss).
		this.$('section:visible').last().find('li:visible').last().addClass('is-last-permissible')

		// Can't show these without an online database
		if (!globals.isOperationalMode()) {
			this.$('.broadcast-alert').addClass('is-not-displayed')
			this.$('.background-process-status').addClass('is-not-displayed')
		}
	},

	/**
	 * Secures the Users, Groups & Permissions section
	 */
	secureUsersGroupsPermissions: function() {
		var users = permissions.modifyUsers() || permissions.deleteUsers()
		if (!users) {
			this.$('.users').addClass('is-not-displayed')
		}
		var groups = permissions.allGroups() || permissions.localAdmin()
		if (!groups) {
			this.$('.groups').addClass('is-not-displayed')
		}
		if (!permissions.anyStructure()) {
			this.$('.org-permissions-audit').addClass('is-not-displayed')
		}
		if (!this.modifySomeDatasetPermission) {
			this.$('.dataset-permissions-audit').addClass('is-not-displayed')
		}

		if (!users && !groups && !permissions.anyStructure() &&
			!this.modifySomeDatasetPermission) {
			this.$('.users-groups-permissions').addClass('is-not-displayed')
			// also hide the children of this section
			this.$('.users-groups-permissions').find('li').addClass('is-not-displayed')
		}

	},

	// this is kind of a hack, but when viewing a bookmark of something like
	// a dashboard, the overview route expects this method to be here
	toggleEdit: _.identity,

	autoRemove: true

})

export default AdminNav

AdminNav.__filename__ = 'admin/admin-nav.js'
